import { ThemedText } from "@/components/themed-text";
import { BrandColors } from "@/constants/theme";
import { allowsNotificationsAsync } from "@/lib/registerForPushNotificationsAsync";
import { syncPushTokenWithServer } from "@/lib/sync-push-token";
import { showToast } from "@/lib/toast";
import { useAuthStore } from "@/stores/use-auth-store";
import Ionicons from "@react-native-vector-icons/ionicons";
import * as Device from "expo-device";
import React, { useState, useSyncExternalStore } from "react";
import {
  ActivityIndicator,
  AppState,
  Linking,
  Platform,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";

const isWeb = Platform.OS === "web";

type NotificationPermissionSnapshot = boolean | null;

let permissionAllowed: NotificationPermissionSnapshot = null;
const permissionListeners = new Set<() => void>();

function emitPermissionChange(): void {
  permissionListeners.forEach((listener) => listener());
}

async function refreshNotificationPermission(): Promise<void> {
  const allowed = await allowsNotificationsAsync();
  if (permissionAllowed === allowed) {
    return;
  }

  permissionAllowed = allowed;
  emitPermissionChange();
}

function subscribeToNotificationPermission(
  onStoreChange: () => void,
): () => void {
  permissionListeners.add(onStoreChange);
  void refreshNotificationPermission();

  const appStateSubscription = AppState.addEventListener(
    "change",
    (nextState) => {
      if (nextState === "active") {
        void refreshNotificationPermission();
      }
    },
  );

  return () => {
    permissionListeners.delete(onStoreChange);
    appStateSubscription.remove();
  };
}

function getNotificationPermissionSnapshot(): NotificationPermissionSnapshot {
  return permissionAllowed;
}

function getNotificationPermissionServerSnapshot(): NotificationPermissionSnapshot {
  return null;
}

export const NotificationBanner = () => {
  const user = useAuthStore((state) => state.user);
  const [loading, setLoading] = useState(false);
  const permissionAllowedSnapshot = useSyncExternalStore(
    subscribeToNotificationPermission,
    getNotificationPermissionSnapshot,
    getNotificationPermissionServerSnapshot,
  );
  const visible = permissionAllowedSnapshot === false;

  if (isWeb || !Device.isDevice || !user || user.fcm_token || !visible) {
    return null;
  }

  const handleEnableNotifications = async () => {
    setLoading(true);
    const enableNotification = await allowsNotificationsAsync();
    if (!enableNotification) {
      showToast.error("Please enable notifications in settings to continue.");
      setTimeout(async () => {
        await Linking.openSettings();
      }, 1000);
      setLoading(false);
      return;
    }

    try {
      await syncPushTokenWithServer({
        requestPermission: true,
      });
      await refreshNotificationPermission();
    } catch (error: unknown) {
      console.error("Failed to enable notifications:", error);
      showToast.error(
        error instanceof Error
          ? error.message
          : "Failed to enable notifications",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.contentContainer}>
        <TouchableOpacity
          style={styles.btn}
          onPress={handleEnableNotifications}
          disabled={loading}
        >
          <View style={styles.content}>
            <View style={styles.iconContainer}>
              <Ionicons
                name="notifications-outline"
                size={24}
                color={BrandColors.white}
              />
            </View>
            <View style={styles.textContainer}>
              <ThemedText style={styles.title}>Enable Notifications</ThemedText>
              <ThemedText style={styles.subtitle}>
                Stay updated on your booking status and messages.
              </ThemedText>
            </View>
            <View style={styles.actionContainer}>
              {loading ? (
                <ActivityIndicator size="small" color={BrandColors.white} />
              ) : (
                <Ionicons
                  name="chevron-forward"
                  size={20}
                  color={BrandColors.white}
                />
              )}
            </View>
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 10,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
  },
  contentContainer: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 16,
  },
  btn: {
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: BrandColors.primary,
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "rgba(255, 255, 255, 0.2)",
    justifyContent: "center",
    alignItems: "center",
  },
  textContainer: {
    flex: 1,
  },
  title: {
    color: BrandColors.white,
    fontSize: 15,
    fontWeight: "700",
    marginBottom: 2,
  },
  subtitle: {
    color: BrandColors.white,
    fontSize: 12,
    opacity: 0.9,
    lineHeight: 16,
  },
  actionContainer: {
    width: 24,
    alignItems: "center",
    justifyContent: "center",
  },
});
